import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Check, 
  Zap, 
  Target, 
  Crown, 
  Star,
  ArrowRight,
  Globe,
  CreditCard,
  Loader2,
  X,
  ShieldCheck
} from 'lucide-react';
import NavBar from '../components/NavBar';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pricing data in 3 currencies
const PRICING = {
  executive_scan: {
    TRY: { monthly: 0, yearly: 0 },
    USD: { monthly: 0, yearly: 0 },
    EUR: { monthly: 0, yearly: 0 },
    isFree: true
  },
  operational: {
    TRY: { monthly: 2990, yearly: 29900 },
    USD: { monthly: 99, yearly: 990 },
    EUR: { monthly: 89, yearly: 890 },
    isFree: false
  },
  enterprise: {
    TRY: { monthly: 7990, yearly: 79900 },
    USD: { monthly: 249, yearly: 2490 },
    EUR: { monthly: 229, yearly: 2290 },
    isFree: false
  },
  strategic: {
    TRY: { monthly: 14990, yearly: 149900 },
    USD: { monthly: 499, yearly: 4990 },
    EUR: { monthly: 459, yearly: 4590 },
    isFree: false
  }
};

const CURRENCY_SYMBOLS = {
  TRY: '₺',
  USD: '$',
  EUR: '€'
};

const PricingPage = () => {
  const { language } = useLanguage();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState('TRY');
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  
  // Checkout state
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [buyerForm, setBuyerForm] = useState({
    buyer_name: '',
    buyer_surname: '',
    buyer_email: '',
    buyer_phone: '',
    buyer_address: '',
    buyer_city: 'Istanbul',
    buyer_country: 'Turkey',
    buyer_identity_number: '11111111111'
  });
  const [subscription, setSubscription] = useState(null);
  
  useEffect(() => {
    fetchTiers();
    if (isAuthenticated) fetchSubscription();
  }, []);
  
  const fetchSubscription = async () => {
    try {
      const res = await axios.get(`${API}/subscriptions/current`);
      setSubscription(res.data);
    } catch (err) {
      console.error('Error fetching subscription:', err);
    }
  };
  
  const fetchTiers = async () => {
    try {
      const response = await axios.get(`${API}/assessment-tiers`);
      setTiers(response.data);
    } catch (error) {
      console.error('Error fetching tiers:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const getTierIcon = (tierId) => {
    switch (tierId) {
      case 'executive_scan':
        return <Zap className="h-8 w-8" />;
      case 'operational':
        return <Target className="h-8 w-8" />;
      case 'enterprise':
        return <Crown className="h-8 w-8" />;
      case 'strategic':
        return <Star className="h-8 w-8" />;
      default:
        return <Target className="h-8 w-8" />;
    }
  };
  
  const getTierGradient = (tierId) => {
    switch (tierId) {
      case 'executive_scan':
        return 'from-slate-500 to-slate-600';
      case 'operational':
        return 'from-blue-500 to-blue-600';
      case 'enterprise':
        return 'from-purple-500 to-purple-600';
      case 'strategic':
        return 'from-amber-500 to-amber-600';
      default:
        return 'from-blue-500 to-blue-600';
    }
  };
  
  const getBorderColor = (tierId, isRecommended) => {
    if (isRecommended) return 'border-purple-500';
    return 'border-slate-700';
  };
  
  const formatPrice = (price) => {
    return new Intl.NumberFormat(language === 'tr' ? 'tr-TR' : 'en-US').format(price);
  };
  
  const getPrice = (tierId) => {
    const pricing = PRICING[tierId];
    if (pricing.isFree) {
      return language === 'tr' ? 'Ücretsiz' : 'Free';
    }
    const price = pricing[currency][billingPeriod];
    return `${CURRENCY_SYMBOLS[currency]}${formatPrice(price)}`;
  };
  
  const getPriceSubtext = (tierId) => {
    const pricing = PRICING[tierId];
    if (pricing.isFree) {
      return language === 'tr' ? 'Kredi kartı gerektirmez' : 'No credit card required';
    }
    if (billingPeriod === 'yearly') {
      const monthlyEquivalent = pricing[currency].yearly / 12;
      return `${CURRENCY_SYMBOLS[currency]}${formatPrice(Math.round(monthlyEquivalent))}/${language === 'tr' ? 'ay' : 'mo'}`;
    }
    return language === 'tr' ? 'aylık faturalandırma' : 'billed monthly';
  };
  
  const getYearlySavings = (tierId) => {
    const pricing = PRICING[tierId];
    if (pricing.isFree) return 0;
    const monthlyTotal = pricing[currency].monthly * 12;
    const yearlyPrice = pricing[currency].yearly;
    return Math.round(((monthlyTotal - yearlyPrice) / monthlyTotal) * 100);
  };
  
  const handleSelectTier = (tier) => {
    if (PRICING[tier.id]?.isFree) {
      if (isAuthenticated) {
        navigate('/assessments');
      } else {
        navigate('/auth');
      }
      return;
    }
    if (!isAuthenticated) {
      toast.info(language === 'tr' ? 'Lütfen önce giriş yapın' : 'Please login first');
      navigate('/auth');
      return;
    }
    setSelectedTier(tier);
    setBuyerForm(prev => ({
      ...prev,
      buyer_name: user?.full_name?.split(' ')[0] || '',
      buyer_surname: user?.full_name?.split(' ').slice(1).join(' ') || '',
      buyer_email: user?.email || ''
    }));
    setShowCheckout(true);
  };
  
  const handlePayment = async () => {
    if (!buyerForm.buyer_name || !buyerForm.buyer_surname || !buyerForm.buyer_email || !buyerForm.buyer_phone || !buyerForm.buyer_address) {
      toast.error(language === 'tr' ? 'Lütfen tüm alanları doldurun' : 'Please fill all fields');
      return;
    }
    setPaymentLoading(true);
    try {
      const res = await axios.post(`${API}/payments/initiate`, {
        tier: selectedTier.id,
        currency: currency,
        callback_url: `${window.location.origin}/payment/callback`,
        ...buyerForm
      });
      if (res.data.status === 'success' && res.data.payment_page_url) {
        window.location.href = res.data.payment_page_url;
      } else if (res.data.status === 'free') {
        toast.success(language === 'tr' ? 'Bu paket ücretsiz!' : 'This tier is free!');
        navigate('/assessments');
      }
    } catch (err) {
      console.error('Payment init error:', err);
      toast.error(language === 'tr' ? 'Ödeme başlatılamadı. Lütfen daha sonra tekrar deneyin.' : 'Payment could not be initiated. Please try again later.');
    } finally {
      setPaymentLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <NavBar />
        <div className="pt-24 flex items-center justify-center">
          <div className="text-white">{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-950" data-testid="pricing-page">
      <NavBar />
      
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            {language === 'tr' ? 'Değerlendirme Paketleri' : 'Assessment Packages'}
          </h1>
          <p className="text-xl text-slate-400">
            {language === 'tr' 
              ? 'İşletmenizin ihtiyaçlarına uygun değerlendirme paketini seçin'
              : 'Choose the assessment package that fits your business needs'}
          </p>
        </div>
        
        {/* Currency & Billing Toggle */}
        <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-8 mb-12">
          {/* Currency Selector */}
          <div className="flex items-center space-x-2">
            <Globe className="h-5 w-5 text-slate-400" />
            <div className="flex rounded-lg bg-slate-800/50 p-1">
              {['TRY', 'USD', 'EUR'].map((curr) => (
                <button
                  key={curr}
                  onClick={() => setCurrency(curr)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    currency === curr
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {CURRENCY_SYMBOLS[curr]} {curr}
                </button>
              ))}
            </div>
          </div>
          
          {/* Billing Period Toggle */}
          <div className="flex rounded-lg bg-slate-800/50 p-1">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                billingPeriod === 'monthly'
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {language === 'tr' ? 'Aylık' : 'Monthly'}
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-colors relative ${
                billingPeriod === 'yearly'
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {language === 'tr' ? 'Yıllık' : 'Yearly'}
              <span className="absolute -top-2 -right-2 px-2 py-0.5 text-xs rounded-full bg-emerald-500 text-white">
                -17%
              </span>
            </button>
          </div>
        </div>
        
        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {tiers.map((tier) => (
            <Card 
              key={tier.id}
              className={`relative bg-slate-900/50 ${getBorderColor(tier.id, tier.is_recommended)} border-2 transition-all hover:scale-[1.02] ${
                tier.is_recommended ? 'ring-2 ring-purple-500/50' : ''
              }`}
              data-testid={`pricing-tier-${tier.id}`}
            >
              {/* Recommended Badge */}
              {tier.is_recommended && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-purple-500 text-white text-sm font-medium">
                  {language === 'tr' ? 'Önerilen' : 'Recommended'}
                </div>
              )}
              
              {tier.badge === 'flagship' && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-amber-500 text-white text-sm font-medium">
                  {language === 'tr' ? 'Tam Kapsamlı' : 'Full Coverage'}
                </div>
              )}
              
              <CardHeader className="text-center pb-4">
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${getTierGradient(tier.id)} flex items-center justify-center mx-auto mb-4 text-white`}>
                  {getTierIcon(tier.id)}
                </div>
                
                <CardTitle className="text-xl text-white">
                  {language === 'tr' ? tier.name_tr : tier.name_en}
                </CardTitle>
                
                <CardDescription className="text-slate-400 mt-2 min-h-[60px]">
                  {language === 'tr' ? tier.description_tr : tier.description_en}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-6">
                {/* Price */}
                <div className="text-center">
                  <div className="text-4xl font-bold text-white">
                    {getPrice(tier.id)}
                  </div>
                  <div className="text-sm text-slate-400 mt-1">
                    {getPriceSubtext(tier.id)}
                  </div>
                  {billingPeriod === 'yearly' && !PRICING[tier.id].isFree && (
                    <div className="mt-2 inline-flex items-center px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-xs">
                      {language === 'tr' ? 'Yıllık %' : 'Save '}{getYearlySavings(tier.id)}% {language === 'tr' ? 'tasarruf' : ''}
                    </div>
                  )}
                </div>
                
                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 py-4 border-y border-slate-700/50">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white">{tier.question_count[0]}-{tier.question_count[1]}</div>
                    <div className="text-xs text-slate-400">{language === 'tr' ? 'Soru' : 'Questions'}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white">{tier.duration_minutes[0]}-{tier.duration_minutes[1]}</div>
                    <div className="text-xs text-slate-400">{language === 'tr' ? 'Dakika' : 'Minutes'}</div>
                  </div>
                </div>
                
                {/* Features */}
                <ul className="space-y-3">
                  {(language === 'tr' ? tier.features_tr : tier.features_en).map((feature, i) => (
                    <li key={i} className="flex items-start text-sm">
                      <Check className="h-5 w-5 text-emerald-400 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-300">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                {/* CTA Button */}
                <Button 
                  onClick={() => handleSelectTier(tier)}
                  className={`w-full ${
                    subscription?.tier === tier.id
                      ? 'bg-emerald-600 hover:bg-emerald-700'
                      : tier.is_recommended 
                        ? 'bg-purple-600 hover:bg-purple-700' 
                        : tier.badge === 'flagship'
                          ? 'bg-amber-600 hover:bg-amber-700'
                          : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                  data-testid={`select-tier-${tier.id}`}
                >
                  {subscription?.tier === tier.id
                    ? (language === 'tr' ? 'Mevcut Paketiniz' : 'Current Plan')
                    : PRICING[tier.id].isFree 
                      ? (language === 'tr' ? 'Ücretsiz Başla' : 'Start Free')
                      : (language === 'tr' ? 'Paketi Seç' : 'Choose Plan')
                  }
                  {subscription?.tier !== tier.id && <ArrowRight className="ml-2 h-4 w-4" />}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
        
        {/* FAQ / Additional Info */}
        <div className="max-w-3xl mx-auto mt-16 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            {language === 'tr' ? 'Sıkça Sorulan Sorular' : 'Frequently Asked Questions'}
          </h2>
          
          <div className="space-y-4 text-left mt-8">
            <div className="p-4 rounded-lg bg-slate-800/30 border border-slate-700/50">
              <h3 className="font-medium text-white mb-2">
                {language === 'tr' ? 'Hangi paketi seçmeliyim?' : 'Which package should I choose?'}
              </h3>
              <p className="text-sm text-slate-400">
                {language === 'tr' 
                  ? 'Dijital dönüşüm yolculuğunuzun başındaysanız Executive Scan ile hızlı bir görüntü alabilirsiniz. Yapılandırılmış dönüşüm için Enterprise Değerlendirme önerilir.'
                  : 'If you are at the beginning of your digital transformation journey, Executive Scan provides a quick snapshot. For structured transformation, Enterprise Assessment is recommended.'}
              </p>
            </div>
            
            <div className="p-4 rounded-lg bg-slate-800/30 border border-slate-700/50">
              <h3 className="font-medium text-white mb-2">
                {language === 'tr' ? 'Değerlendirme sonuçları nasıl kullanılır?' : 'How are assessment results used?'}
              </h3>
              <p className="text-sm text-slate-400">
                {language === 'tr' 
                  ? 'Sonuçlar AI destekli kapsamlı bir rapor olarak sunulur. Bu rapor yönetim kurulu sunumları, stratejik planlama ve yatırım önceliklendirmesi için kullanılabilir.'
                  : 'Results are presented as an AI-powered comprehensive report. This report can be used for board presentations, strategic planning, and investment prioritization.'}
              </p>
            </div>
            
            <div className="p-4 rounded-lg bg-slate-800/30 border border-slate-700/50">
              <h3 className="font-medium text-white mb-2">
                {language === 'tr' ? 'Kurumsal fiyatlandırma var mı?' : 'Is there enterprise pricing?'}
              </h3>
              <p className="text-sm text-slate-400">
                {language === 'tr' 
                  ? 'Evet, çoklu değerlendirme ve white-label ihtiyaçları için özel kurumsal planlar sunuyoruz. Detaylar için bizimle iletişime geçin.'
                  : 'Yes, we offer custom enterprise plans for multiple assessments and white-label needs. Contact us for details.'}
              </p>
            </div>
          </div>
        </div>
      </main>
      
      {/* Checkout Modal */}
      {showCheckout && selectedTier && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="checkout-modal">
          <Card className="bg-slate-900 border-slate-700 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-blue-400" />
                    {language === 'tr' ? 'Ödeme Bilgileri' : 'Payment Details'}
                  </CardTitle>
                  <CardDescription className="text-slate-400 mt-1">
                    {language === 'tr' ? selectedTier.name_tr : selectedTier.name_en} — {getPrice(selectedTier.id)}
                  </CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowCheckout(false)} className="text-slate-400 hover:text-white">
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">{language === 'tr' ? 'Ad' : 'First Name'}</Label>
                  <Input
                    value={buyerForm.buyer_name}
                    onChange={(e) => setBuyerForm(p => ({ ...p, buyer_name: e.target.value }))}
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                    data-testid="buyer-name-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">{language === 'tr' ? 'Soyad' : 'Last Name'}</Label>
                  <Input
                    value={buyerForm.buyer_surname}
                    onChange={(e) => setBuyerForm(p => ({ ...p, buyer_surname: e.target.value }))}
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                    data-testid="buyer-surname-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Email</Label>
                <Input
                  type="email"
                  value={buyerForm.buyer_email}
                  onChange={(e) => setBuyerForm(p => ({ ...p, buyer_email: e.target.value }))}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                  data-testid="buyer-email-input"
                />
              </div>
              <div>
                <Label className="text-slate-300">{language === 'tr' ? 'Telefon' : 'Phone'}</Label>
                <Input
                  value={buyerForm.buyer_phone}
                  onChange={(e) => setBuyerForm(p => ({ ...p, buyer_phone: e.target.value }))}
                  placeholder="+905xxxxxxxxx"
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                  data-testid="buyer-phone-input"
                />
              </div>
              <div>
                <Label className="text-slate-300">{language === 'tr' ? 'Adres' : 'Address'}</Label>
                <Input
                  value={buyerForm.buyer_address}
                  onChange={(e) => setBuyerForm(p => ({ ...p, buyer_address: e.target.value }))}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                  data-testid="buyer-address-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">{language === 'tr' ? 'Şehir' : 'City'}</Label>
                  <Input
                    value={buyerForm.buyer_city}
                    onChange={(e) => setBuyerForm(p => ({ ...p, buyer_city: e.target.value }))}
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                    data-testid="buyer-city-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">{language === 'tr' ? 'Ülke' : 'Country'}</Label>
                  <Input
                    value={buyerForm.buyer_country}
                    onChange={(e) => setBuyerForm(p => ({ ...p, buyer_country: e.target.value }))}
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                  />
                </div>
              </div>
              
              <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-blue-400 flex-shrink-0" />
                <p className="text-xs text-blue-300">
                  {language === 'tr' 
                    ? 'Ödemeniz iyzico altyapısı ile güvenli şekilde işlenecektir.'
                    : 'Your payment will be securely processed via iyzico.'}
                </p>
              </div>
              
              <div className="flex justify-end space-x-3 pt-2">
                <Button variant="outline" onClick={() => setShowCheckout(false)} className="border-slate-700 text-slate-300">
                  {language === 'tr' ? 'İptal' : 'Cancel'}
                </Button>
                <Button 
                  onClick={handlePayment} 
                  disabled={paymentLoading}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="pay-now-btn"
                >
                  {paymentLoading ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> {language === 'tr' ? 'İşleniyor...' : 'Processing...'}</>
                  ) : (
                    <><CreditCard className="h-4 w-4 mr-2" /> {language === 'tr' ? 'Ödemeye Geç' : 'Proceed to Pay'}</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-slate-800">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm text-slate-500">
            © 2024 GDMII Platform. {language === 'tr' ? 'Tüm hakları saklıdır.' : 'All rights reserved.'}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
